<template>
  <ContentWrap>
    <el-form :inline="true" :model="query" class="-mb-15px" @submit.prevent>
      <el-form-item label="用户昵称">
        <el-input
          v-model="query.userNickname"
          clearable
          class="!w-180px"
          placeholder="请输入用户昵称"
          @keyup.enter="search"
        />
      </el-form-item>
      <el-form-item label="动态">
        <el-input
          v-model="query.feedContent"
          clearable
          class="!w-220px"
          placeholder="请输入动态内容"
          @keyup.enter="search"
        />
      </el-form-item>
      <el-form-item label="评论内容">
        <el-input
          v-model="query.keyword"
          clearable
          class="!w-220px"
          placeholder="请输入评论内容"
          @keyup.enter="search"
        />
      </el-form-item>
      <el-form-item label="评论时间">
        <el-date-picker
          v-model="commentRange"
          type="datetimerange"
          value-format="YYYY-MM-DD HH:mm:ss"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          class="!w-360px"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search"
          ><Icon icon="ep:search" class="mr-5px" />查询</el-button
        >
        <el-button @click="reset"><Icon icon="ep:refresh" class="mr-5px" />重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <div class="mb-12px">
      <el-button
        v-hasPermi="['bosscircle:comment:audit']"
        type="success"
        plain
        :disabled="!selectedRows.length"
        @click="batchStatus(1)"
        ><Icon icon="ep:circle-check" class="mr-5px" />批量启用</el-button
      >
      <el-button
        v-hasPermi="['bosscircle:comment:audit']"
        type="warning"
        plain
        :disabled="!selectedRows.length"
        @click="batchStatus(2)"
        ><Icon icon="ep:circle-close" class="mr-5px" />批量禁用</el-button
      >
      <el-button
        v-hasPermi="['bosscircle:comment:delete']"
        type="danger"
        plain
        :disabled="!selectedRows.length"
        @click="deleteSelected"
        ><Icon icon="ep:delete" class="mr-5px" />批量删除</el-button
      >
      <span v-if="selectedRows.length" class="ml-10px text-12px text-#909399"
        >已选择 {{ selectedRows.length }} 项</span
      >
    </div>
    <el-table
      v-loading="loading"
      :data="list"
      row-key="id"
      @selection-change="selectedRows = $event"
    >
      <el-table-column type="selection" width="45" />
      <el-table-column prop="userNickname" label="用户昵称" min-width="140" show-overflow-tooltip />
      <el-table-column prop="feedContent" label="动态" min-width="220" show-overflow-tooltip />
      <el-table-column label="评论内容" min-width="280" show-overflow-tooltip>
        <template #default="{ row }"
          ><span v-if="row.replyToUserNickname" class="text-#409eff"
            >回复 @{{ row.replyToUserNickname }}：</span
          >{{ row.content }}</template
        >
      </el-table-column>
      <el-table-column prop="likeCount" label="点赞数" width="85" />
      <el-table-column label="状态" width="100">
        <template #default="{ row }"
          ><el-tag :type="row.status === 1 ? 'success' : row.status === 2 ? 'info' : 'warning'">{{
            statusText(row.status)
          }}</el-tag></template
        >
      </el-table-column>
      <el-table-column prop="createTime" label="评论时间" width="180" :formatter="dateFormatter" />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-button
            v-hasPermi="['bosscircle:comment:audit']"
            link
            :type="row.status === 1 ? 'warning' : 'success'"
            @click="setStatus(row)"
            >{{ row.status === 1 ? '禁用' : '启用' }}</el-button
          >
          <el-button
            v-hasPermi="['bosscircle:comment:delete']"
            link
            type="danger"
            @click="remove(row.id)"
            >删除</el-button
          >
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>
</template>

<script setup lang="ts">
import * as Api from '@/api/business/comment'
import { dateFormatter } from '@/utils/formatTime'

defineOptions({ name: 'BossCircleComment' })

const message = useMessage()
const loading = ref(false)
const list = ref<Api.CommentVO[]>([])
const selectedRows = ref<Api.CommentVO[]>([])
const total = ref(0)
const commentRange = ref<string[]>([])
const query = reactive<Api.CommentPageReqVO>({
  pageNo: 1,
  pageSize: 10,
  keyword: '',
  userNickname: '',
  feedContent: ''
})
const statusText = (status: number) => ['待审核', '启用', '禁用'][status] || '未知'
const load = async () => {
  loading.value = true
  try {
    const data = await Api.getCommentPage({
      ...query,
      createTimeStart: commentRange.value?.[0],
      createTimeEnd: commentRange.value?.[1]
    })
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}
const search = () => {
  query.pageNo = 1
  load()
}
const reset = () => {
  Object.assign(query, { pageNo: 1, keyword: '', userNickname: '', feedContent: '' })
  commentRange.value = []
  load()
}
const setStatus = async (row: Api.CommentVO) => {
  const status = row.status === 1 ? 2 : 1
  await message.confirm(`确认${status === 1 ? '启用' : '禁用'}该评论吗？`)
  await Api.updateCommentStatus(row.id, status)
  message.success('操作成功')
  await load()
}
const batchStatus = async (status: number) => {
  await message.confirm(
    `确认${status === 1 ? '启用' : '禁用'}选中的 ${selectedRows.value.length} 条评论吗？`
  )
  await Api.batchUpdateCommentStatus(
    selectedRows.value.map((item) => item.id),
    status
  )
  selectedRows.value = []
  message.success('批量操作成功')
  await load()
}
const remove = async (id: number) => {
  await message.delConfirm('确认删除该评论吗？')
  await Api.deleteComment(id)
  message.success('删除成功')
  await load()
}
const deleteSelected = async () => {
  await message.delConfirm(`确认删除选中的 ${selectedRows.value.length} 条评论吗？`)
  await Api.batchDeleteComment(selectedRows.value.map((item) => item.id))
  selectedRows.value = []
  message.success('批量删除成功')
  await load()
}

onMounted(load)
</script>
